import {doc, getDoc} from "firebase/firestore";
import {db} from "../Security/firebase-config";
import {parseSetupKey, resolveSetupEvent} from "./eventSetup";
import {normalizeFocusPoint} from "./teamPlanning";

export function eventPrepIdentity(event = {}, eventId = "") {
  const raw = event.eventId || eventId;
  const setup = parseSetupKey(raw);
  const occurrence = String(raw).match(/^(.*)__(\d{4}-\d{2}-\d{2})$/);
  const source = event.sourceEventId || setup?.eventId || occurrence?.[1] || raw;
  const date = event.instanceDate || setup?.instanceDate || occurrence?.[2];
  return {scope: setup?.scope || event.calendar || "team", key: date ? `${source}__${date}` : source};
}

export function prepRecordFocusPoints(record) {
  let content = record?.content;
  try {if (typeof content === "string") content = JSON.parse(content);} catch {return [];}
  const section = content?.sections?.focus;
  if (!section) return [];
  if (Array.isArray(section.entries)) return section.entries;
  return String(section.fields?.points?.value || "").split("\n")
    .map(text => ({text: text.replace(/^\s*[-•]\s*/, "").trim()})).filter(point => point.text);
}

function asDraft(points) {
  const occurrences = new Map();
  return (Array.isArray(points) ? points : []).map((raw, index) => {
    const point = typeof raw === "string" ? {text: raw} : raw || {};
    // Stable identity lets live updates reorder points without confusing them
    // with locally edited or removed rows in the open recording setup.
    const identity = String(point.id || JSON.stringify([point.sourceType, point.sourceScope, point.sourceMapId, point.sourceId, point.text, point.scope, point.side, point.assigneeUids]));
    let hash = 2166136261;
    for (const character of identity) hash = Math.imul(hash ^ character.charCodeAt(0), 16777619);
    const key = (hash >>> 0).toString(16);
    const occurrence = occurrences.get(key) || 0;
    occurrences.set(key, occurrence + 1);
    return {
      ...normalizeFocusPoint(point, index), id: `temp-event-prep-${key}-${occurrence}`,
      ratings: {}, outcome: "not_attempted", outcomeNote: "", evidence: [],
    };
  }).filter(point => point.text);
}

// A saved modern prep, including an intentionally empty list, takes precedence.
export async function loadEventPrepFocusPoints({teamId, event = {}, eventId = ""}) {
  const {scope, key} = eventPrepIdentity(event, eventId);
  if (!teamId || !key || scope !== "team") return [];
  try {
    const prep = await getDoc(doc(db, "teams", teamId, "eventPreps", key));
    if (prep.exists()) return asDraft(prepRecordFocusPoints(prep.data()));
  } catch (error) {
    if (error.code !== "permission-denied") throw error;
    // Keep older deployments usable until their prep read rules are deployed.
  }
  if (event.sessionPlan) return asDraft(event.sessionPlan.focusPoints);
  const legacyKey = event.sessionPlanId || key;
  try {
    const legacy = await getDoc(doc(db, "teams", teamId, "sessionPlans", legacyKey));
    if (legacy.exists()) return asDraft(legacy.data()?.focusPoints);
  } catch (error) {
    if (error.code !== "permission-denied") throw error;
  }
  if (event.eventId) return [];
  const team = await getDoc(doc(db, "teams", teamId));
  const occurrence = key.match(/^(.*)__(\d{4}-\d{2}-\d{2})$/);
  const lookup = occurrence ? `setup:team:${encodeURIComponent(occurrence[1])}:${occurrence[2]}:map-1` : key;
  return asDraft(resolveSetupEvent(team.data()?.events, lookup)?.sessionPlan?.focusPoints);
}
